
import { useState } from 'react';

interface SearchAndFiltersProps {
  search: string;
  categoryFilter: string;
  statusFilter: string;
  updatedFilter: string;
  categoryOptions: Array<{ id: string; nome: string }>;
  onSearchChange: (search: string) => void;
  onCategoryFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onUpdatedFilterChange: (value: string) => void;
  onClearFilters: () => void;
}

export function SearchAndFilters({
  search,
  categoryFilter,
  statusFilter,
  updatedFilter,
  categoryOptions,
  onSearchChange,
  onCategoryFilterChange,
  onStatusFilterChange,
  onUpdatedFilterChange,
  onClearFilters,
}: SearchAndFiltersProps) {
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const hasActiveFilters = Boolean(search || categoryFilter || statusFilter || updatedFilter);

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <label htmlFor="search" className="sr-only">
            Buscar destinatários
          </label>
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </span>
          <input
            id="search"
            type="text"
            placeholder="Buscar por nome, telefone, processo ou mandado..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-12 pr-4 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:ring-4 focus:ring-slate-100"
            aria-label="Campo de busca para destinatários"
          />
        </div>

        <div className="mt-4 hidden items-center gap-3 lg:flex">
          <select
            value={categoryFilter}
            onChange={(e) => onCategoryFilterChange(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
            aria-label="Filtrar por categoria"
          >
            <option value="">Categoria</option>
            {categoryOptions.map((category) => (
              <option key={category.id} value={category.id}>
                {category.nome}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
            aria-label="Filtrar por status"
          >
            <option value="">Status</option>
            <option value="completo">Completo</option>
            <option value="pendente">Pendente</option>
          </select>

          <select
            value={updatedFilter}
            onChange={(e) => onUpdatedFilterChange(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
            aria-label="Filtrar por data de atualização"
          >
            <option value="">Data de atualização</option>
            <option value="today">Hoje</option>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
          </select>

          <button
            onClick={onClearFilters}
            className="ml-auto rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
            disabled={!hasActiveFilters}
          >
            Limpar filtros
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between lg:hidden">
          <button
            onClick={() => setIsMobileFiltersOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6h16M7 12h10m-7 6h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            Filtros
          </button>

          <button
            onClick={onClearFilters}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
            disabled={!hasActiveFilters}
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {isMobileFiltersOpen && (
        <div className="fixed inset-0 z-40 bg-slate-950/50 lg:hidden" onClick={() => setIsMobileFiltersOpen(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <p className="text-base font-semibold text-slate-900">Filtros</p>
              <button
                onClick={() => setIsMobileFiltersOpen(false)}
                className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                Fechar
              </button>
            </div>

            <div className="space-y-3">
              <select
                value={categoryFilter}
                onChange={(e) => onCategoryFilterChange(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                aria-label="Filtrar por categoria"
              >
                <option value="">Categoria</option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.nome}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                aria-label="Filtrar por status"
              >
                <option value="">Status</option>
                <option value="completo">Completo</option>
                <option value="pendente">Pendente</option>
              </select>

              <select
                value={updatedFilter}
                onChange={(e) => onUpdatedFilterChange(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                aria-label="Filtrar por data de atualizacao"
              >
                <option value="">Data de atualização</option>
                <option value="today">Hoje</option>
                <option value="7d">Últimos 7 dias</option>
                <option value="30d">Últimos 30 dias</option>
              </select>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => {
                  onClearFilters();
                  setIsMobileFiltersOpen(false);
                }}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Limpar
              </button>
              <button
                onClick={() => setIsMobileFiltersOpen(false)}
                className="flex-1 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
